import classNames from "classnames";
import React, { useEffect } from "react";
import useSessionStorage from "../hooks/useSessionStorage";
import { STRINGS } from "../strings";
import { getLanguage, langMap, translate } from "../translate";
import { MEDFLYT_LOGO } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { AppDimmer } from "./Generic/AppDimmer";
import { AppIcon } from "./Generic/AppIcon";
import { AppOverlay } from "./Generic/AppOverlay";
import AppSlideFadeIn from "./Generic/AppSlideFadeIn";
import Flex from "./Generic/Flex";
import { IsDefined } from "./Generic/IsDefined";
import PopupVideo from "./PopupVideo";

interface Props {
  closeVideo: () => void;
}

function getVideoUrl(language: string) {
  const url = "https://d3l0mkr2ggho68.cloudfront.net/mobile-assets/agencyvideo/agencyvideo.m3u8";
  const selectedLanguage = langMap[language];

  switch (selectedLanguage) {
    case langMap.en:
    case langMap.es:
    case langMap.ru:
    case langMap.zh: {
      return url.replace("$lang", selectedLanguage.toLocaleLowerCase());
    }
    default: {
      return url.replace("$lang", langMap.en.toLocaleLowerCase());
    }
  }
}

const AgencyVideo = (props: Props) => {
  const [isPlaying, setPlaying] = React.useState<boolean>(false);
  const [video, setVideo] = React.useState<{ url: string; thumbnailUrl: string }>();
  const [isVideoEnded, setVideoEnded] = React.useState<boolean>(false);
  const [playedSeconds, setPlayedSeconds] = useSessionStorage("agency-video-seconds", 0);
  const [logoClickCounter, setLogoClickCounter] = React.useState(0);
  const [isErrorMessageShown, setErrorMessageShown] = React.useState(false);

  const handlePlay = (isPlayingValue: boolean) => {
    setErrorMessageShown(false);
    setPlaying(isPlayingValue);
  };

  const continueButtonString = (() => {
    if (isVideoEnded) {
      return translate(STRINGS.CONTINUE_TO_APP);
    }
    return `${translate(STRINGS.CONTINUE_TO_APP)} (${(120 - playedSeconds).toFixed(0)})`;
  })();

  const handleReplay = () => {
    setErrorMessageShown(false);
    setPlayedSeconds(0);
    setPlaying(true);
  };

  const handleClickLogo = () => {
    const newCounter = logoClickCounter + 1;

    setLogoClickCounter(newCounter);

    if (newCounter === 5) {
      setVideoEnded(true);
    }
  };

  const handleClickContinue = () => {
    if (isVideoEnded) {
      props.closeVideo();
      return;
    }

    setErrorMessageShown(true);
  };

  const contentElem = (
    <div
      className={classNames("__content-wrapper", {
        active: !isPlaying,
      })}
    >
      <div className="__content">
        <AppSlideFadeIn delay={300}>
          <h4>{translate(STRINGS.INTRODUCING)}</h4>
        </AppSlideFadeIn>
        <AppSlideFadeIn delay={450}>
          <AppIcon style={{ width: 130 }} size={32} icon={MEDFLYT_LOGO} onClick={handleClickLogo} />
        </AppSlideFadeIn>
        <div className="__buttons">
          <AppSlideFadeIn delay={600}>
            <Flex direction="row" xGap={20}>
              <AppButton onClick={handleReplay}>
                <span>{translate(STRINGS.REPLAY)}</span>
              </AppButton>
              <div onClick={handleClickContinue} className="__continue">
                <AppButton disabled={!isVideoEnded}>
                  <span>{continueButtonString}</span>
                </AppButton>
                {isErrorMessageShown ? (
                  <span className="__message">
                    {translate(STRINGS.PLEASE_WATCH_THE_FULL_VIDEO_TO_PROCEED_TO_THE_APP)}
                  </span>
                ) : null}
              </div>
            </Flex>
          </AppSlideFadeIn>
        </div>
      </div>
    </div>
  );

  const handleResolveLanguage = (language: string) => {
    const url = getVideoUrl(language);
    const thumbnail = "https://orientation-center-prod.s3.amazonaws.com/passport/preview.jpg";

    setVideo({
      url: url,
      thumbnailUrl: thumbnail,
    });
  };

  useEffect(() => {
    getLanguage().then(handleResolveLanguage);
  }, []);

  const handleError = () => {
    props.closeVideo();
  };

  return (
    <AppOverlay className="PassportVideo">
      <div className="__wrapper">
        <IsDefined value={video}>
          {(definedVideo) => (
            <PopupVideo
              isPlaying={isPlaying}
              url={definedVideo.url}
              thumbnailUrl={definedVideo.thumbnailUrl}
              playedSeconds={playedSeconds}
              onEnded={() => (!isVideoEnded ? setVideoEnded(true) : null)}
              onError={handleError}
              onSetPlaying={handlePlay}
              onSetPlayedSeconds={setPlayedSeconds}
            />
          )}
        </IsDefined>
        {contentElem}
      </div>
      <AppDimmer />
    </AppOverlay>
  );
};

export default AgencyVideo;
